import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInAnonymously } from 'firebase/auth';
import { ArrowLeft, BookOpen, Eye, EyeOff, Lock, ShieldCheck, User, Users } from 'lucide-react';
import { auth, BACKEND_URL } from '../../firebase/config';

const LOGIN_META = {
  hod: {
    title: 'HOD Login',
    subtitle: 'Engineering Sciences & Humanities',
    icon: ShieldCheck,
    accent: 'blue',
    loginLabel: 'HOD Login ID',
    loginPlaceholder: 'hod'
  },
  faculty: {
    title: 'Faculty Login',
    subtitle: 'Faculty workspace',
    icon: Users,
    accent: 'emerald',
    loginLabel: 'Faculty ID',
    loginPlaceholder: 'mentor'
  },
  student: {
    title: 'Student Login',
    subtitle: 'Student dashboard',
    icon: BookOpen,
    accent: 'indigo',
    loginLabel: 'Roll Number',
    loginPlaceholder: 'FB101'
  }
};

const accentClasses = {
  blue: {
    page: 'from-blue-950 via-blue-900 to-slate-900',
    icon: 'bg-blue-100 text-blue-700',
    button: 'bg-blue-700 hover:bg-blue-800 focus:ring-blue-500'
  },
  emerald: {
    page: 'from-emerald-950 via-emerald-900 to-slate-900',
    icon: 'bg-emerald-100 text-emerald-700',
    button: 'bg-emerald-700 hover:bg-emerald-800 focus:ring-emerald-500'
  },
  indigo: {
    page: 'from-indigo-950 via-indigo-900 to-slate-900',
    icon: 'bg-indigo-100 text-indigo-700',
    button: 'bg-indigo-700 hover:bg-indigo-800 focus:ring-indigo-500'
  }
};

const sessionKeys = ['adminSession', 'userSession', 'studentSession'];

const formatDobInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 4),
    digits.slice(4, 8)
  ].filter(Boolean);

  return parts.join('-');
};

const RoleLogin = ({ loginType = 'faculty' }) => {
  const navigate = useNavigate();
  const meta = LOGIN_META[loginType] || LOGIN_META.faculty;
  const styles = accentClasses[meta.accent];
  const Icon = meta.icon;
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const normalizedLoginId = useMemo(() => (
    loginType === 'student' ? loginId.toUpperCase() : loginId.trim()
  ), [loginId, loginType]);

  const storeSession = async (session) => {
    const userCredential = await signInAnonymously(auth);
    const sessionWithUid = {
      ...session,
      uid: userCredential.user.uid
    };

    sessionKeys.forEach((key) => sessionStorage.removeItem(key));

    if (loginType === 'student') {
      sessionStorage.setItem('studentSession', JSON.stringify(sessionWithUid));
      navigate('/student/dashboard');
      return;
    }

    sessionStorage.setItem('userSession', JSON.stringify(sessionWithUid));
    navigate('/dashboard');
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (loginType === 'student' && pin.length !== 6) {
        setError('Enter the 6-digit security PIN.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${BACKEND_URL.replace(/\/$/, '')}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          loginType,
          loginId: normalizedLoginId,
          password,
          pin
        })
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.success) {
        setError(result.error || 'Login failed.');
        return;
      }

      await storeSession(result.session);
    } catch (loginError) {
      console.error('Login error:', loginError);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen bg-gradient-to-br ${styles.page} flex items-center justify-center px-4 py-8`}>
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/')}
          className="mb-6 inline-flex items-center text-white/90 hover:text-white"
          type="button"
        >
          <ArrowLeft className="mr-2 h-5 w-5" />
          Back
        </button>

        <div className="rounded-2xl bg-white p-8 shadow-2xl">
          <div className="mb-8 text-center">
            <div className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full ${styles.icon}`}>
              <Icon className="h-9 w-9" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">{meta.title}</h1>
            <p className="mt-2 text-sm text-gray-600">{meta.subtitle}</p>
          </div>

          {error && (
            <div className="mb-6 rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">{meta.loginLabel}</span>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={loginId}
                  onChange={(event) => setLoginId(loginType === 'student' ? event.target.value.toUpperCase() : event.target.value)}
                  placeholder={meta.loginPlaceholder}
                  className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-4 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-gray-900"
                  required
                />
              </div>
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-gray-700">
                {loginType === 'student' ? 'Date of Birth' : 'Password'}
              </span>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type={loginType === 'student' || showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(loginType === 'student' ? formatDobInput(event.target.value) : event.target.value)}
                  placeholder={loginType === 'student' ? 'DDMMYYYY' : 'Password'}
                  maxLength={loginType === 'student' ? 10 : undefined}
                  inputMode={loginType === 'student' ? 'numeric' : undefined}
                  className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-12 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-gray-900"
                  required
                />
                {loginType !== 'student' && (
                  <button
                    type="button"
                    onClick={() => setShowPassword((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                )}
              </div>
              {loginType === 'student' && (
                <span className="mt-1 block text-xs text-gray-500">Type DOB as DDMMYYYY. Hyphens are added automatically.</span>
              )}
            </label>

            {loginType === 'student' && (
              <label className="block">
                <span className="mb-2 block text-sm font-medium text-gray-700">Security PIN</span>
                <div className="relative">
                  <ShieldCheck className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                  <input
                    type={showPin ? 'text' : 'password'}
                    value={pin}
                    onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    maxLength="6"
                    className="w-full rounded-lg border border-gray-300 py-3 pl-10 pr-12 text-center font-mono text-xl tracking-widest focus:border-transparent focus:outline-none focus:ring-2 focus:ring-gray-900"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPin((value) => !value)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    aria-label={showPin ? 'Hide PIN' : 'Show PIN'}
                  >
                    {showPin ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </label>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full rounded-lg px-4 py-3 font-semibold text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-gray-400 ${styles.button}`}
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleLogin;
